import { FormEvent, useEffect, useState } from "react";
import { fetchRdap, fmtDate, daysUntil, isNetResult, RdapResult } from "../lib/rdap";

function normalizeDomain(input: string): string {
  let s = input.trim().toLowerCase().replace(/\.$/, "");
  // Strip a pasted URL down to its host. IPv6 literals contain colons and no
  // slashes, so this leaves them intact.
  s = s.replace(/^https?:\/\//, "").replace(/\/.*$/, "");
  return s;
}

export function WhoisLookup() {
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<RdapResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = (raw: string) => {
    const d = normalizeDomain(raw);
    if (!d) return;
    setDomain(d);
    setBusy(true);
    setResult(null);
    setError(null);
    const params = new URLSearchParams({ query: d });
    history.replaceState(null, "", `?${params.toString()}`);
    fetchRdap(d)
      .then((r) => setResult(r))
      .catch((e) => setError(String(e?.message ?? e)))
      .finally(() => setBusy(false));
  };

  // Deep-link support: ?domain= on load.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const d = p.get("query") ?? p.get("domain");
    if (d) run(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    run(domain);
  };

  // Split the union once so the two result shapes render independently.
  const net = result && isNetResult(result) ? result : null;
  const dom = result && result.ok && !isNetResult(result) ? result : null;
  const expDays = dom ? daysUntil(dom.expires) : null;

  return (
    <main className="console">
      <div className="input-row" role="search">
        <span className="input-row-label">WHOIS</span>
        <form onSubmit={submit} className="lookup-form">
          <input
            type="text"
            placeholder="example.com, 8.8.8.8, or AS15169"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            autoFocus
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            aria-label="Domain, IP address, or AS number"
          />
          <button type="submit" disabled={busy || !domain.trim()}>
            {busy ? "Looking up…" : "Lookup"}
          </button>
        </form>
        <span className="input-row-hint" aria-hidden="true">
          <span className="stat-dot" /> RDAP · domains, IPs &amp; ASNs
        </span>
      </div>

      {error ? <div className="error" role="alert">{error}</div> : null}

      <div className="body-grid body-grid-single">
        <section className="table-panel">
          <div className="panel-title">
            REGISTRATION DATA
            {dom ? <b> · {dom.domain}</b> : null}
            {net ? <b> · {net.query}</b> : null}
          </div>

          {!result && !busy ? (
            <div className="empty-state">
              <p>Look up a <strong>domain</strong> for its registrar, dates, nameservers and DNSSEC — or an <strong>IP address</strong> or <strong>AS number</strong> for the network it belongs to and who runs it. All via RDAP.</p>
              <p className="empty-hint">Try <code>example.com</code>, <code>8.8.8.8</code>, or <code>AS15169</code>.</p>
            </div>
          ) : null}

          {busy ? <div className="empty-state"><p>Querying RDAP…</p></div> : null}

          {result && !result.ok ? (
            <div className="empty-state">
              <p>{result.message}</p>
              {/* The ccTLD explanation only makes sense for a domain — showing it
                  after an IP lookup failed was just confusing. */}
              {!result.supported && !/^(\d|[0-9a-f]*:|as\d)/i.test(domain) ? (
                <p className="empty-hint">Some country-code TLDs don't publish an RDAP server yet.</p>
              ) : null}
            </div>
          ) : null}

          {dom ? (
            <table className="whois-table">
              <tbody>
                <tr>
                  <th scope="row">Registrar</th>
                  <td>
                    {dom.registrarUrl ? (
                      <a href={dom.registrarUrl} target="_blank" rel="nofollow noopener noreferrer">
                        {dom.registrar ?? dom.registrarUrl}
                      </a>
                    ) : (
                      dom.registrar ?? "—"
                    )}
                  </td>
                </tr>
                <tr>
                  <th scope="row">Registered</th>
                  <td>{fmtDate(dom.registered)}</td>
                </tr>
                <tr>
                  <th scope="row">Expires</th>
                  <td>
                    {fmtDate(dom.expires)}
                    {expDays != null ? (
                      <span className={`exp-badge ${expDays < 30 ? "exp-soon" : ""}`}>
                        {expDays < 0 ? `expired ${-expDays}d ago` : `in ${expDays}d`}
                      </span>
                    ) : null}
                  </td>
                </tr>
                <tr>
                  <th scope="row">Updated</th>
                  <td>{fmtDate(dom.updated)}</td>
                </tr>
                <tr>
                  <th scope="row">DNSSEC</th>
                  <td>{dom.dnssec == null ? "—" : dom.dnssec ? "Signed" : "Unsigned"}</td>
                </tr>
                <tr>
                  <th scope="row">Nameservers</th>
                  <td>
                    {dom.nameservers.length ? (
                      <ul className="ns-list">
                        {dom.nameservers.map((ns) => (
                          <li key={ns}><code>{ns}</code></li>
                        ))}
                      </ul>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
                <tr>
                  <th scope="row">Status</th>
                  <td>
                    {dom.statuses.length ? (
                      <span className="status-codes">{dom.statuses.join(", ")}</span>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : null}

          {net ? (
            <>
              <table className="whois-table">
                <tbody>
                  <tr><th scope="row">{net.kind === "asn" ? "AS number" : "Network"}</th>
                    <td>{net.kind === "asn" ? net.asnRange ?? net.query : net.cidr.length ? net.cidr.map((c) => <code key={c}>{c}</code>) : `${net.startAddress ?? "?"} – ${net.endAddress ?? "?"}`}</td></tr>
                  <tr><th scope="row">Name</th><td>{net.name ?? "—"}</td></tr>
                  <tr><th scope="row">Organisation</th><td>{net.org ?? "—"}</td></tr>
                  <tr><th scope="row">Allocation type</th><td>{net.type ?? "—"}</td></tr>
                  <tr><th scope="row">Country</th><td>{net.country ?? "—"}</td></tr>
                  <tr><th scope="row">Handle</th><td>{net.handle ? <code>{net.handle}</code> : "—"}</td></tr>
                  {net.kind === "ip" && net.startAddress ? (
                    <tr><th scope="row">Range</th><td><code>{net.startAddress} – {net.endAddress}</code></td></tr>
                  ) : null}
                  <tr><th scope="row">Abuse contact</th>
                    <td>{net.abuseEmail ? <a href={`mailto:${net.abuseEmail}`}>{net.abuseEmail}</a> : "—"}</td></tr>
                  {net.events.length ? (
                    <tr><th scope="row">Dates</th>
                      <td>{net.events.map((e) => `${e.action}: ${fmtDate(e.date)}`).join(" · ")}</td></tr>
                  ) : null}
                </tbody>
              </table>

              <p className="whois-cross">
                {net.kind === "ip" ? (
                  <>
                    Look up the reverse DNS for this address:{" "}
                    <a href={`/ptr-lookup?host=${encodeURIComponent(net.query)}`}>PTR lookup</a> · or{" "}
                    <a href={`/subnet-calculator?cidr=${encodeURIComponent(net.cidr[0] ?? net.query)}`}>work out its subnet</a>.
                  </>
                ) : (
                  <>See <a href="/what-is-my-ip">your own IP and ASN</a>, or the <a href="/resolvers">resolver leaderboard</a>.</>
                )}
              </p>
            </>
          ) : null}

          {dom ? (
            <p className="whois-cross">
              Now check where its DNS has propagated:{" "}
              <a href={`/?host=${encodeURIComponent(dom.domain)}&type=A`}>A record</a> ·{" "}
              <a href={`/?host=${encodeURIComponent(dom.domain)}&type=NS`}>NS</a> ·{" "}
              <a href={`/?host=${encodeURIComponent(dom.domain)}&type=MX`}>MX</a>
            </p>
          ) : null}
        </section>
      </div>

    </main>
  );
}
